import {
  Component,
  OnInit,
  OnDestroy,
  Inject,
  HostListener,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { OktaAuth, AuthState } from '@okta/okta-auth-js';
import { IHeaderTitle } from 'src/app/interfaces/IHeaderTitle';
import { IHeaderMenu } from 'src/app/interfaces/IHeaderMenu';
import { AppService } from 'src/app/app.service';
import { IAppConfig } from 'src/app/models/config';
import { OktaUserInfoService } from 'src/app/core/okta-user-info.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false,
})
export class HeaderComponent implements OnInit, OnDestroy {
  profile = '';
  appConfig: IAppConfig = {} as IAppConfig;
  title: IHeaderTitle = { name: 'DataDrop' } as IHeaderTitle;

  listMenuItems: IHeaderMenu[] = [];

  isMenuOpen = false;
  menuOpen = false;
  isMobile = window.innerWidth <= 1320;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly appService: AppService,
    @Inject(OKTA_AUTH) private readonly oktaAuth: OktaAuth,
    private readonly authStateService: OktaAuthStateService,
    private readonly router: Router,
    private readonly oktaUserInfoService: OktaUserInfoService
  ) {}

  ngOnInit(): void {
    this.appConfig = this.appService.readConfig() || ({} as IAppConfig);
    console.log('[HeaderComponent] App config loaded:', this.appConfig);
    this.title = {
      name: this.appConfig.applicationName || 'DataDrop',
    };

    this.setupMenuItems();

    this.authStateService.authState$
      .pipe(
        takeUntil(this.destroy$),
        filter(
          (authState: AuthState) =>
            !!authState && authState.isAuthenticated === true
        )
      )
      .subscribe({
        next: () => {
          this.loadUserInfo();
        },
        error: (error) => {
          console.error('[HeaderComponent] Auth state error:', error);
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupMenuItems(): void {
    this.listMenuItems = [
      {
        label: 'Logout',
        eventName: 'logout',
      },
    ];
  }

  private loadUserInfo(): void {
    this.oktaUserInfoService
      .getUserInfo()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (user) => {
          if (user && user.preferred_username) {
            console.log(
              '[HeaderComponent] User info loaded:',
              user.preferred_username
            );
            this.profile = user.preferred_username;

            if (!this.appConfig.isFederated) {
              this.listMenuItems.unshift({
                label: 'Configure MFA',
                eventName: 'configureMFA',
              });
            }
          }
        },
        error: (err) => {
          console.error('[HeaderComponent] Error fetching user info:', err);
        },
      });
  }

  onMenuItemClick(eventName: string) {
    this.closeMenu();

    switch (eventName) {
      case 'logout':
        this.onLogout();
        break;
      case 'configureMFA':
        this.navigateTo(this.appConfig.gatewayAccountSettingsUrl);
        break;
      default:
        console.warn('Unknown menu item clicked:', eventName);
    }
  }

  navigateTo(url: string) {
    if (url) {
      window.open(url, '_blank');
    }
  }

public async onLogout() {
    try {
      localStorage.removeItem('loggedin_users');
      localStorage.removeItem('okta-token-storage');
      localStorage.removeItem('okta-cache-storage');
      sessionStorage.clear();

      this.oktaUserInfoService.clearCache();
      
      await this.oktaAuth.signOut({
        postLogoutRedirectUri: window.location.origin + '/logout',
      });
    } catch (error) {
      console.error('[HeaderComponent] Logout error:', error);

      this.router.navigate(['/logout'], { queryParams: { next: '/file-explorer' }});
    }
  }

  onTitleClick() {
    console.log('[HeaderComponent] Title clicked, navigating to gateway login URL');  
     this.router.navigateByUrl('/file-explorer');
  }

  onTitleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onTitleClick();
    }
  }

  toggleMenu() {
    this.isMenuOpen = !this.isMenuOpen;
    this.menuOpen = this.isMenuOpen;
  }

  closeMenu() {
    this.isMenuOpen = false;
    this.menuOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.header-profile')) {
      this.closeMenu();
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    const width = (event.target as Window).innerWidth;
    this.isMobile = width <= 1320;
    if (width > 1320 && this.isMenuOpen) {
      this.closeMenu();
    }
  }
}
